<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Role'): $t('Edit Role')  }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Role Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-4">
            <label
              for="name"
              class="form-label"
            >
              {{ $t('Role Name') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="name"
                type="text"
                v-model.trim="v$.form.name.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter role name') + '...'"
                :disabled="isDefault"
              >
              <div v-for="error of v$.form.name.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <label
              for="description"
              class="form-label"
            >
              {{ $t('Role Description') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="description"
                type="text"
                v-model.trim="v$.form.description.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter role description') + '...'"
                :disabled="isDefault"
              >
              <div v-for="error of v$.form.description.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-gears me-2" />
        {{ $t('Permissions & Access Control') }}
      </div>
      <div class="px-3">
        <table class="border table table-sm">
          <template
            v-for="(category, index) in permissionCategories"
            :key="index"
          >
            <thead class="table-light">
              <tr>
                <th
                  colspan="3"
                >
                  {{ $t(category.name) }}
                </th>
              </tr>
            </thead>
            <tbody
              class="align-baseline"
            >
              <!-- เรนเดอร์เฉพาะ group ที่มีข้อมูล permission จาก API — กันหน้าพังถ้า FE/BE list ไม่ตรงกัน -->
              <tr
                v-for="(group, index) in category.groups.filter(g => permissions.some(p => p.group === g))"
              >
                <td
                >
                  {{ $t(group) }}
                </td>
                <td
                  class="border-start"
                >
                  <div class="form-check form-switch">
                    <input
                      class="form-check-input shadow-none"
                      type="checkbox"
                      :id="'all-'+group+index"
                      :checked="isAllSelected(permissions.find((permission) => permission.group == group))"
                      @change="handleSelectAll(permissions.find((permission) => permission.group == group), $event)"
                      :disabled="isDefault"
                    >
                    <label class="form-check-label small fw-bold text-secondary cursor-pointer" :for="'all-'+group+index">
                      {{ $t('All') }}
                    </label>
                  </div>
                </td>
                <td
                  class="border-start"
                >
                  <div class="row g-3 ps-md-5">
                    <div
                      v-for="perm in permissions.find((permission) => permission.group == group).permissions"
                      :key="perm.id"
                      :class="[perm.title == 'View Confidential'? 'col-12 col-md-4': 'col-6 col-md-2']"
                    >
                      <div class="form-check">
                        <input
                          class="form-check-input shawdow-none"
                          style="box-shadow: none !important;"
                          type="checkbox"
                          :id="'perm-'+perm.id"
                          :value="perm.id"
                          v-model="v$.form.permission_ids.$model"
                          @change="handlePermissionChange(permissions.find((permission) => permission.group == group), perm)"
                          :disabled="isDefault"
                        >
                        <label class="form-check-label small cursor-pointer" :for="'perm-'+perm.id">
                          {{ $t(perm.title) }}
                        </label>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </table>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid || isDefault"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid || isDefault"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Roles } from '@/services/endpoint/users.service';
import numeral from 'numeral';
import Select2 from 'vue3-select2-component';
import { event } from 'jquery';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      isDefault: false,
      form: {
        name: '',
        description: '',
        permission_ids: []
      },
      permissions: [],
      permissionCategories: [
        {
          name: 'Overview',
          groups: [
            'Dashboard'
          ]
        },
        {
          name: 'POS & Storefront Operation',
          groups: [
            'POS System',
            'Shift Management',
            'Kitchen Display System (KDS)',
            'Bill Management',
            'Tax Invoice',
          ]
        },
        {
          name: 'Inventory Management',
          groups: [
            'Inventory',
            'Inventory Recipe',
            'Inventory Production Batch',
            'Good Receipt',
            'Stock Adjustment',
          ]
        },
        {
          name: 'Menu & Products',
          groups: [
            'Product',
            'Product Category',
            'Modifier',
            'Attribute'
          ]
        },
        {
          name: 'Store Setup',
          groups: [
            'Branch',
            'Zone',
            'Table',
            'Delivery Platform'
          ]
        },
        {
          name: 'Marketing & Customers',
          groups: [
            'Customer',
            'Customer Group',
            'Promotion',
            'Loyalty'
          ]
        },
        {
          name: 'Finance & Payments',
          groups: [
            'Expense'
          ]
        },
        {
          name: 'User & System',
          groups: [
            'User',
            'Role',
            'Setting'
          ]
        },
        {
          name: 'Partners',
          groups: [
            'Supplier'
          ]
        },
        {
          name: 'Reports',
          groups: [
            'Report Sales Summary',
            'Report Sales By Hour',
            'Report Profit',
            'Report Expense',
            'Report Goods Receipt',
            'Report Sales By Branch',
            'Report Product Sales',
            'Report Category Sales',
            'Report Payment Methods',
            'Report Tax',
            'Report Discount',
            'Report Promotion Performance',
            'Report Staff Performance',
            'Report Cancelled Orders',
            'Report Stock Movement',
            'Report Waste',
            'Report VAT Sales',
            'Report Tax Invoice Register',
            'Report VAT Purchase',
            'Report Material Usage'
          ]
        },
      ]
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    AccessDenied,
    Select2
  },
  methods: {
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getCreateData() {
      await Roles.getCreate().then(res => {
        this.permissions = res.data.data.permissions.map(function(group) {
          return {
            group: group.group,
            permissions: group.permissions.map(perm => ({
              ...perm,
              isView: perm.name.endsWith('_view')
            }))
          }
        });
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    async getUpdateData() {
      await Roles.getUpdate(this.id).then(res => {
        this.permissions = res.data.filter.permissions.map(function(group) {
          return {
            group: group.group,
            permissions: group.permissions.map(perm => ({
              ...perm,
              isView: perm.name.endsWith('_view')
            }))
          }
        });
        this.form.name = res.data.data.name;
        this.form.description = res.data.data.description;
        this.form.permission_ids = res.data.data.permission_ids;
        this.isDefault = res.data.data.default;

        this.hasPermission = true;
      }).catch(err => {
        this.hasPermission = false;
        this.loading = false;
			  throw err;
      });
    },
    setParamsBeforeSendCreate() {
      let params = _.cloneDeep(this.form);
      return $.each(params, (key, value) => {
        if (value === null || value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    setParamsBeforeSendUpdate() {
      let params = _.cloneDeep(this.form);
      return $.each(params, (key, value) => {
        if (value === null || value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid || this.isDefault) {
        this.loadingSave = false;
        return;
      }
      await Roles.update(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Role!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      this.loadingSave = false;
      await Roles.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Role!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
    isAllSelected(group) {
      const groupIds = group.permissions.map(p => p.id);
      return groupIds.every(id => this.form.permission_ids.includes(id));
    },
    handleSelectAll(group, event) {
      const isChecked = event.target.checked;
      const groupIds = group.permissions.map(p => p.id);

      if (isChecked) {
        groupIds.forEach(id => {
          if (!this.form.permission_ids.includes(id)) {
            this.form.permission_ids.push(id);
          }
        });
      } else {
        this.form.permission_ids = this.form.permission_ids.filter(id => !groupIds.includes(id));
      }
    },
    handlePermissionChange(group, perm) {
      const isChecked = this.form.permission_ids.includes(perm.id);
      if (perm.isView) {
        if (!isChecked) {
          const groupIds = group.permissions.map(p => p.id);
          this.form.permission_ids = this.form.permission_ids.filter(id => !groupIds.includes(id));
        }
      } else {
        if (isChecked) {
          const viewPerm = group.permissions.find(p => p.isView);
          if (viewPerm && !this.form.permission_ids.includes(viewPerm.id)) {
            this.form.permission_ids.push(viewPerm.id);
          }
        }
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  },
  computed: {
  }
}
</script>
